import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area 
} from 'recharts';
import { 
  ArrowLeft, TrendingUp, BookOpen, Clock, Trophy, Target, Calendar, 
  Award, Flame, Star, Crown, Medal, Sun, Moon, Users, Bell, 
  CheckCircle, AlertCircle, ChevronRight, Home, UserCheck, GraduationCap, LogOut,
  Zap, Brain, FileText, Lightbulb, Menu, X, Shield, Timer, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import FeedbackButton from '@/components/FeedbackButton';
import ParentInsights from '@/components/ParentInsights';
import { startImpersonation } from '@/utils/parentImpersonation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Badge icon mapping
const BADGE_ICONS = {
  trophy: Trophy,
  flame: Flame,
  crown: Crown,
  star: Star,
  book: BookOpen,
  medal: Medal,
  sun: Sun,
  moon: Moon,
};

// Available practice paper categories
const PRACTICE_CATEGORIES = [
  { id: 'seven-plus', name: '7+', subjects: ['maths', 'english', 'reasoning'] },
  { id: 'eleven-plus', name: '11+', subjects: ['maths', 'english', 'verbal-reasoning', 'non-verbal-reasoning'] },
  { id: 'year-9', name: 'Year 9', subjects: ['maths', 'english', 'science', 'history', 'geography'] },
  { id: 'year-10', name: 'Year 10', subjects: ['maths', 'english', 'science', 'history', 'geography'] },
  { id: 'gcse', name: 'GCSE', subjects: ['maths', 'english', 'sciences'] },
  { id: 'a-levels', name: 'A-Levels', subjects: ['maths-a', 'physics-a', 'chemistry-a', 'biology-a', 'english-lit-a'] },
];

const ParentDashboard = ({ progress, updateProgress }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [childData, setChildData] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [challengerData, setChallengerData] = useState(null);
  const [topicAnalysis, setTopicAnalysis] = useState(null);
  const [linkedChildren, setLinkedChildren] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [childEmail, setChildEmail] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [activeChildId, setActiveChildId] = useState(null);
  const [activeTab, setActiveTab] = useState('capabilities');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sscsSettings, setSscsSettings] = useState({
    sscs_enabled: true,
    suggest_break_minutes: 45,
    enforce_lock_minutes: 60,
    break_duration_minutes: 10
  });
  const [sscsAnalytics, setSscsAnalytics] = useState(null);
  const [sscsSaving, setSscsSaving] = useState(false);

  const handleLogout = () => {
    // Navigate first, then clear state to prevent route guard redirect
    navigate('/');
    // Use setTimeout to ensure navigation completes before clearing auth
    setTimeout(() => {
      localStorage.clear();
      if (updateProgress) {
        updateProgress(null);
      }
    }, 100);
  };

  useEffect(() => {
    loadParentData();
  }, []);

  const loadParentData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const childrenRes = await axios.get(`${API}/parent/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinkedChildren(childrenRes.data.children || []);
      
      if (childrenRes.data.children && childrenRes.data.children.length > 0) {
        const firstChildId = childrenRes.data.children[0].id;
        setActiveChildId(firstChildId);
        await loadChildData(firstChildId);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading parent data:', error);
      setLoading(false);
    }
  };

  const loadChildData = async (childId) => {
    try {
      const token = localStorage.getItem('token');
      
      const [dashboardRes, streakRes, challengerRes, topicAnalysisRes] = await Promise.all([
        axios.get(`${API}/parent/child/${childId}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/parent/child/${childId}/streak`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/parent/child/${childId}/challenger`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/parent/child/${childId}/topic-analysis`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setChildData(dashboardRes.data);
      setStreakData(streakRes.data);
      setChallengerData(challengerRes.data);
      setTopicAnalysis(topicAnalysisRes.data);
      setActiveChildId(childId);
      
      // Load SSCS settings and analytics
      try {
        const [sscsRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/session-control/parent-controls/${childId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null })),
          axios.get(`${API}/session-control/analytics`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null }))
        ]);
        if (sscsRes.data) {
          setSscsSettings({
            sscs_enabled: sscsRes.data.sscs_enabled !== false,
            suggest_break_minutes: sscsRes.data.suggest_break_minutes || 45,
            enforce_lock_minutes: sscsRes.data.enforce_lock_minutes || 60,
            break_duration_minutes: sscsRes.data.break_duration_minutes || 10
          });
        }
        if (analyticsRes.data) setSscsAnalytics(analyticsRes.data);
      } catch (e) { /* SSCS data load is non-critical */ }
    } catch (error) {
      console.error('Error loading child data:', error);
      toast.error('Failed to load child progress data');
    }
  };


  const saveSscsSettings = async () => {
    if (!activeChildId) return;
    setSscsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/session-control/parent-controls/${activeChildId}`, {
        suggest_break_minutes: sscsSettings.suggest_break_minutes,
        enforce_lock_minutes: sscsSettings.enforce_lock_minutes,
        break_duration_minutes: sscsSettings.break_duration_minutes,
        enabled: sscsSettings.sscs_enabled
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Screen time settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setSscsSaving(false);
  };

  const linkChild = async (e) => {
    e.preventDefault();
    if (!childEmail.trim()) return;
    
    setLinkLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/parent/link-child`,
        { childEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Child account linked successfully!');
      setLinkedChildren([...linkedChildren, response.data.child]);
      setShowLinkForm(false);
      setChildEmail('');
      
      await loadChildData(response.data.child.id);
    } catch (error) {
      console.error('Error linking child:', error);
      toast.error(error.response?.data?.detail || 'Failed to link child account');
    } finally {
      setLinkLoading(false);
    }
  };

  const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

  const [impersonating, setImpersonating] = useState(false);

  const handleViewAsChild = async (childId, route = '/welcome') => {
    if (!childId) return;
    setImpersonating(true);
    try {
      const child = await startImpersonation(BACKEND_URL, childId);
      toast.success(`Now viewing as ${child.fullName || child.full_name || 'your child'}`);
      // Hard navigate so App.js re-bootstraps the identity from localStorage cleanly.
      window.location.replace(route);
    } catch (err) {
      console.error('Impersonation failed:', err);
      toast.error(err.response?.data?.detail || 'Could not switch to child view');
      setImpersonating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If no children linked
  if (linkedChildren.length === 0) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 px-4 md:py-6 md:px-8 shadow-lg">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-white hover:bg-white/20 p-2 md:px-4">
                <Home className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Home</span>
              </Button>
              <div className="flex gap-2">
                <Button onClick={() => navigate('/tutor-collaboration')} className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-xs md:text-sm px-2 md:px-4" data-testid="tutor-button">
                  <Users className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Find a Tutor</span>
                </Button>
                <FeedbackButton userType="parent" variant="default" />
                <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/20 p-2 md:px-4" data-testid="logout-button">
                  <LogOut className="h-4 w-4 md:mr-2" /> <span className="hidden md:inline">Logout</span>
                </Button>
              </div>
            </div>
            <h1 className="text-2xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Parent Dashboard</h1>
            <p className="text-white/90 text-sm md:text-base">Track your child's learning progress</p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto p-4 md:p-8">
          <Card className="shadow-2xl" data-testid="link-child-card">
            <CardHeader className="text-center">
              <div className="bg-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-10 w-10 text-purple-600" />
              </div>
              <CardTitle className="text-2xl">Link Your Child's Account</CardTitle>
              <CardDescription>Connect to your child's Edpies account to monitor their progress.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={linkChild} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="childEmail">Child's Email Address</Label>
                  <Input id="childEmail" type="email" placeholder="Enter your child's registered email" value={childEmail} onChange={(e) => setChildEmail(e.target.value)} required data-testid="child-email-input" />
                </div>
                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={linkLoading} data-testid="link-child-btn">
                  {linkLoading ? 'Linking...' : 'Link Account'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const stats = childData || { totalTests: 0, averageScore: 0, timeSpent: 0, strongestSubject: 'N/A', weakestSubject: 'N/A', recentTests: [], subjectProgress: [], performanceTrend: [], weeklyActivity: [] };
  const currentChild = linkedChildren.find(c => c.id === activeChildId) || linkedChildren[0];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 px-4 md:py-6 md:px-8 shadow-lg">
        <div className="max-w-7xl mx-auto">
          {/* Mobile Header */}
          <div className="flex items-center justify-between md:hidden mb-3">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-white hover:bg-white/20 p-2">
              <Home className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-bold">Parent Dashboard</h1>
            <Button 
              variant="ghost" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
              className="text-white hover:bg-white/20 p-2"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white/10 rounded-lg p-3 mb-4 space-y-2">
              <Button 
                onClick={() => { navigate('/subscription'); setMobileMenuOpen(false); }} 
                className="w-full justify-start bg-gradient-to-r from-yellow-500 to-amber-500"
              >
                <Crown className="mr-2 h-4 w-4" /> Upgrade Plan
              </Button>
              <Button 
                onClick={() => { navigate('/tutor-collaboration'); setMobileMenuOpen(false); }} 
                className="w-full justify-start bg-gradient-to-r from-green-500 to-emerald-500"
              >
                <Users className="mr-2 h-4 w-4" /> Find a Tutor
              </Button>
              <FeedbackButton userType="parent" variant="default" />
              <Button 
                variant="ghost" 
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }} 
                className="w-full justify-start text-white hover:bg-white/20"
              >
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </Button>
            </div>
          )}
          
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-white hover:bg-white/20">
              <Home className="mr-2 h-4 w-4" /> Home
            </Button>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/subscription')} className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600" data-testid="upgrade-subscription-btn">
                <Crown className="mr-2 h-4 w-4" /> Upgrade Plan
              </Button>
              <Button onClick={() => navigate('/tutor-collaboration')} className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600" data-testid="find-tutor-btn">
                <Users className="mr-2 h-4 w-4" /> Find a Tutor
              </Button>
              <FeedbackButton userType="parent" variant="default" />
              <Button variant="ghost" onClick={handleLogout} className="text-white hover:bg-white/20" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </Button>
            </div>
          </div>
          
          {/* Title Section */}
          <div className="flex items-center gap-3 md:gap-4">
            <div className="bg-white/20 p-2 md:p-3 rounded-xl"><UserCheck className="h-6 w-6 md:h-8 md:w-8" /></div>
            <div>
              <h1 className="hidden md:block text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Parent Dashboard</h1>
              <p className="text-white/90 text-sm md:text-base">Monitoring: {currentChild?.name || 'Your Child'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
        {/* Child Selector */}
        {linkedChildren.length > 1 && (
          <Card className="shadow-xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-gray-600 font-medium">Viewing:</span>
                {linkedChildren.map((child) => (
                  <Button key={child.id} variant={activeChildId === child.id ? 'default' : 'outline'} onClick={() => loadChildData(child.id)} className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" /> {child.name}
                  </Button>
                ))}
                <Button variant="ghost" onClick={() => setShowLinkForm(true)} className="text-purple-600">+ Add Another Child</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View as Child — quick-switch into the student-side experience */}
        {currentChild && (
          <Card
            className="shadow-xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50"
            data-testid="view-as-child-card"
          >
            <CardContent className="p-4 md:p-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex-shrink-0 bg-gradient-to-br from-amber-500 to-orange-500 text-white p-2.5 rounded-xl">
                  <Eye className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm md:text-base">
                    Browse Edpies as <span className="text-amber-700">{currentChild.name}</span>
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                    See exactly what your child sees — resources, activities, mock papers and all their progress.
                    Switch back to your Parent Dashboard anytime.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewAsChild(currentChild.id, '/welcome')}
                  disabled={impersonating}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="view-as-child-dashboard-btn"
                >
                  <GraduationCap className="h-4 w-4 mr-1.5" />
                  Student Home
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewAsChild(currentChild.id, '/resources-hub')}
                  disabled={impersonating}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="view-as-child-resources-btn"
                >
                  <BookOpen className="h-4 w-4 mr-1.5" />
                  Resources
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleViewAsChild(currentChild.id, '/categories')}
                  disabled={impersonating}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  data-testid="view-as-child-activities-btn"
                >
                  <Eye className="h-4 w-4 mr-1.5" />
                  View All Activities
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Overview Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="shadow-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white" data-testid="stat-tests">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-white/80">Tests Completed</p><p className="text-4xl font-bold">{stats.totalTests}</p></div>
                <BookOpen className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white" data-testid="stat-average">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-white/80">Average Score</p><p className="text-4xl font-bold">{stats.averageScore}%</p></div>
                <Trophy className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl bg-gradient-to-br from-orange-500 to-red-500 text-white" data-testid="stat-streak">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-white/80">Study Streak</p><p className="text-4xl font-bold">{streakData?.currentStreak || 0} days</p></div>
                <Flame className="h-12 w-12 opacity-80 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl bg-gradient-to-br from-yellow-500 to-orange-500 text-white" data-testid="stat-challenger">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-white/80">Challenger Best</p><p className="text-4xl font-bold">{challengerData?.best_score || 0}</p></div>
                <Zap className="h-12 w-12 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Insights Section */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="shadow-xl" data-testid="strongest-subject-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 p-4 rounded-full"><TrendingUp className="h-8 w-8 text-green-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Strongest Subject</p>
                  <p className="text-2xl font-bold text-green-600">{stats.strongestSubject}</p>
                  <p className="text-sm text-gray-600">Your child excels in this area!</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-xl" data-testid="weakest-subject-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-amber-100 p-4 rounded-full"><AlertCircle className="h-8 w-8 text-amber-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Needs More Practice</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.weakestSubject || 'N/A'}</p>
                  <p className="text-sm text-gray-600">Consider extra support here</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for detailed views */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Mobile: Dropdown selector */}
          <div className="md:hidden mb-4">
            <select 
              className="w-full p-3 border rounded-lg bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
            >
              <option value="capabilities">Capabilities</option>
              <option value="insights">Insights</option>
              <option value="progress">Progress</option>
              <option value="subjects">Subjects</option>
              <option value="challenger">Challenger</option>
              <option value="papers">Papers</option>
              <option value="screen-time">Screen Time</option>
              <option value="badges">Badges</option>
            </select>
          </div>
          
          {/* Desktop: Tab buttons */}
          <TabsList className="hidden md:grid md:grid-cols-8 w-full mb-6">
            <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
            <TabsTrigger value="insights">Insights</TabsTrigger>
            <TabsTrigger value="progress">Progress</TabsTrigger>
            <TabsTrigger value="subjects">Subjects</TabsTrigger>
            <TabsTrigger value="challenger">Challenger</TabsTrigger>
            <TabsTrigger value="papers">Papers</TabsTrigger>
            <TabsTrigger value="screen-time" data-testid="screen-time-tab">Screen Time</TabsTrigger>
            <TabsTrigger value="badges">Badges</TabsTrigger>
          </TabsList>

          {/* NEW: Capabilities Tab with ParentInsights */}
          <TabsContent value="capabilities" className="space-y-4 md:space-y-6">
            <ParentInsights childId={activeChildId} />
          </TabsContent>

          {/* NEW: Insights Tab with Topic Analysis */}
          <TabsContent value="insights" className="space-y-4 md:space-y-6">
            {/* Learning Tips & Focus Areas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Card className="shadow-xl border-l-4 border-l-purple-500" data-testid="learning-tips-card">
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Lightbulb className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                    Learning Tips
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">Personalized recommendations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 md:space-y-4 p-4 md:p-6 pt-0 md:pt-0">
                  {topicAnalysis?.learningTips?.length > 0 ? (
                    topicAnalysis.learningTips.map((tip, idx) => (
                      <div key={idx} className={`p-3 md:p-4 rounded-lg ${
                        tip.priority === 'positive' ? 'bg-green-50 border-l-4 border-green-500' :
                        tip.priority === 'high' ? 'bg-red-50 border-l-4 border-red-500' :
                        'bg-amber-50 border-l-4 border-amber-500'
                      }`}>
                        <div className="flex items-start gap-2 md:gap-3">
                          {tip.priority === 'positive' ? <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600 mt-0.5 flex-shrink-0" /> :
                           tip.priority === 'high' ? <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-red-600 mt-0.5 flex-shrink-0" /> :
                           <Target className="h-4 w-4 md:h-5 md:w-5 text-amber-600 mt-0.5 flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800 text-sm md:text-base">{tip.title}</p>
                            <p className="text-xs md:text-sm text-gray-600 mt-1">{tip.message}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4 text-sm">Complete more tests for tips</p>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-xl border-l-4 border-l-blue-500" data-testid="focus-areas-card">
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <Target className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                    Focus Areas
                  </CardTitle>
                  <CardDescription className="text-xs md:text-sm">Topics needing attention</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 md:space-y-3 p-4 md:p-6 pt-0 md:pt-0">
                  {topicAnalysis?.focusAreas?.length > 0 ? (
                    topicAnalysis.focusAreas.map((area, idx) => (
                      <div key={idx} className="p-2 md:p-3 bg-gray-50 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 mb-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800 text-sm md:text-base truncate">{area.topic}</p>
                            <p className="text-xs text-gray-500">{area.subject}</p>
                          </div>
                          <Badge variant="outline" className="text-blue-600 border-blue-300 text-xs self-start">
                            {area.currentAccuracy}% → {area.targetAccuracy}%
                          </Badge>
                        </div>
                        <Progress value={area.currentAccuracy} className="h-2 mb-2" />
                        <p className="text-xs text-gray-600">{area.recommendation}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No focus areas identified yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Strengths & Improvements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Card className="shadow-xl" data-testid="strength-topics-card">
                <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-t-lg p-4 md:p-6">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <TrendingUp className="h-4 w-4 md:h-5 md:w-5" />
                    Strength Topics
                  </CardTitle>
                  <CardDescription className="text-white/80 text-xs md:text-sm">Topics where child excels</CardDescription>
                </CardHeader>
                <CardContent className="pt-4 p-4 md:p-6">
                  {topicAnalysis?.strengthTopics?.length > 0 ? (
                    <div className="space-y-2 md:space-y-3">
                      {topicAnalysis.strengthTopics.map((topic, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 md:p-3 bg-green-50 rounded-lg gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-800 text-sm md:text-base truncate">{topic.topic}</p>
                            <p className="text-xs text-gray-500">{topic.subject} • {topic.correct}/{topic.total}</p>
                          </div>
                          <Badge className="bg-green-600 text-xs flex-shrink-0">{topic.accuracy}%</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4 text-sm">Complete more tests to identify strengths</p>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-xl" data-testid="improvement-topics-card">
                <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg p-4 md:p-6">
                  <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                    <AlertCircle className="h-4 w-4 md:h-5 md:w-5" />
                    Needs Improvement
                  </CardTitle>
                  <CardDescription className="text-white/80 text-xs md:text-sm">Topics to practice more</CardDescription>
                </CardHeader>
                <CardContent className="pt-4 p-4 md:p-6">
                  {topicAnalysis?.improvementTopics?.length > 0 ? (
                    <div className="space-y-2 md:space-y-3">
                      {topicAnalysis.improvementTopics.map((topic, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 md:p-3 bg-amber-50 rounded-lg gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-800 text-sm md:text-base truncate">{topic.topic}</p>
                            <p className="text-xs text-gray-500">{topic.subject} • {topic.wrong} mistakes</p>
                          </div>
                          <Badge className="bg-amber-600 text-xs flex-shrink-0">{topic.accuracy}%</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4 text-sm">No weak areas - great job!</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Subject-wise Analysis */}
            <Card className="shadow-xl" data-testid="subject-analysis-card">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <BookOpen className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  Subject Performance
                </CardTitle>
                <CardDescription className="text-xs md:text-sm">Breakdown by subject</CardDescription>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
                {topicAnalysis?.subjectAnalysis?.length > 0 ? (
                  <div className="space-y-4">
                    {topicAnalysis.subjectAnalysis.map((subject, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${subject.averageScore >= 70 ? 'bg-green-100' : subject.averageScore >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
                              <BookOpen className={`h-5 w-5 ${subject.averageScore >= 70 ? 'text-green-600' : subject.averageScore >= 50 ? 'text-amber-600' : 'text-red-600'}`} />
                            </div>
                            <div>
                              <p className="font-semibold text-lg">{subject.subject}</p>
                              <p className="text-sm text-gray-500">{subject.testsCompleted} tests completed</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-purple-600">{subject.averageScore}%</p>
                            <p className="text-xs text-gray-500">Avg Score</p>
                          </div>
                        </div>
                        <Progress value={subject.averageScore} className="h-2 mb-3" />
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          {subject.strengthTopics?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-green-600 mb-1">Strong in:</p>
                              <div className="flex flex-wrap gap-1">
                                {subject.strengthTopics.slice(0, 2).map((t, i) => (
                                  <Badge key={i} variant="outline" className="text-green-600 border-green-300 text-xs">{t.topic}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {subject.improvementTopics?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-amber-600 mb-1">Needs work:</p>
                              <div className="flex flex-wrap gap-1">
                                {subject.improvementTopics.slice(0, 2).map((t, i) => (
                                  <Badge key={i} variant="outline" className="text-amber-600 border-amber-300 text-xs">{t.topic}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-6">No subject data available yet</p>
                )}
              </CardContent>
            </Card>

            {/* Overall Insights Summary */}
            {topicAnalysis?.overallInsights && (
              <Card className="shadow-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-4 gap-6 text-center">
                    <div>
                      <p className="text-white/80 text-sm">Current Average</p>
                      <p className="text-3xl font-bold">{topicAnalysis.overallInsights.averageScore}%</p>
                    </div>
                    <div>
                      <p className="text-white/80 text-sm">Target Score</p>
                      <p className="text-3xl font-bold">{topicAnalysis.overallInsights.targetScore}%</p>
                    </div>
                    <div>
                      <p className="text-white/80 text-sm">Score Trend</p>
                      <p className="text-3xl font-bold flex items-center justify-center gap-2">
                        {topicAnalysis.overallInsights.scoreTrend === 'improving' ? (
                          <><TrendingUp className="h-6 w-6" /> Up</>
                        ) : topicAnalysis.overallInsights.scoreTrend === 'declining' ? (
                          <><TrendingUp className="h-6 w-6 rotate-180" /> Down</>
                        ) : (
                          <>— Stable</>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/80 text-sm">Questions Attempted</p>
                      <p className="text-3xl font-bold">{topicAnalysis.overallInsights.totalQuestionsAttempted}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="progress" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" />Performance Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={stats.performanceTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Area type="monotone" dataKey="score" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" />Weekly Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.weeklyActivity || [{ day: 'Mon', tests: 0 }, { day: 'Tue', tests: 0 }, { day: 'Wed', tests: 0 }, { day: 'Thu', tests: 0 }, { day: 'Fri', tests: 0 }, { day: 'Sat', tests: 0 }, { day: 'Sun', tests: 0 }]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="tests" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="subjects" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader><CardTitle>Subject Performance Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {stats.subjectProgress && stats.subjectProgress.length > 0 ? (
                    stats.subjectProgress.map((subject, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                            <span className="font-semibold">{subject.name}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-purple-600">{subject.score}%</span>
                            <p className="text-xs text-gray-500">{subject.tests} tests</p>
                          </div>
                        </div>
                        <Progress value={subject.score} className="h-3" />
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No subject data available yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="challenger" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2"><Zap className="h-6 w-6" />Challenger Mode Stats</CardTitle>
                <CardDescription className="text-white/90">AI-powered adaptive difficulty challenges</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {challengerData && challengerData.total_sessions > 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-purple-50 p-4 rounded-xl text-center">
                        <Brain className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-purple-800">{challengerData.best_difficulty}</p>
                        <p className="text-xs text-purple-600">Max Difficulty</p>
                      </div>
                      <div className="bg-yellow-50 p-4 rounded-xl text-center">
                        <Trophy className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-yellow-800">{challengerData.best_score}</p>
                        <p className="text-xs text-yellow-600">Best Score</p>
                      </div>
                      <div className="bg-orange-50 p-4 rounded-xl text-center">
                        <Flame className="h-6 w-6 text-orange-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-orange-800">{challengerData.best_streak}</p>
                        <p className="text-xs text-orange-600">Best Streak</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl text-center">
                        <Target className="h-6 w-6 text-green-600 mx-auto mb-1" />
                        <p className="text-2xl font-bold text-green-800">{challengerData.average_accuracy}%</p>
                        <p className="text-xs text-green-600">Avg Accuracy</p>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3">Recent Sessions ({challengerData.total_sessions} total)</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {challengerData.recent_sessions?.map((session, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium">{session.subject} - {session.stage}</p>
                              <p className="text-xs text-gray-500">{new Date(session.completed_at).toLocaleDateString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-purple-600">{session.score} pts</p>
                              <p className="text-xs text-gray-500">Level {session.max_difficulty}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Zap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">No Challenger Sessions Yet</h3>
                    <p className="text-gray-500">Encourage your child to try Challenger Mode for adaptive AI questions!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="papers" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Available Practice Papers</CardTitle>
                <CardDescription>Recommend papers based on your child's progress</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {PRACTICE_CATEGORIES.map((category) => (
                    <Card key={category.id} className="border-2 hover:border-purple-300 transition-colors">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">{category.name}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {category.subjects.slice(0, 3).map((subject) => (
                            <div key={subject} className="flex items-center justify-between text-sm">
                              <span className="capitalize">{subject.replace(/-/g, ' ')}</span>
                              <Badge variant="outline" className="text-xs">10 papers</Badge>
                            </div>
                          ))}
                          {category.subjects.length > 3 && (
                            <p className="text-xs text-gray-500">+{category.subjects.length - 3} more subjects</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Recent Test History */}
                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Recent Test History</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {stats.recentTests && stats.recentTests.length > 0 ? (
                      stats.recentTests.map((test, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{test.type}</Badge>
                              <span className="font-semibold">{test.subject}</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{test.date}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${test.score >= 70 ? 'text-green-600' : test.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                              {test.score}%
                            </p>
                            <Badge variant={test.passed ? 'default' : 'destructive'}>{test.passed ? 'Passed' : 'Review'}</Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No tests completed yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Screen Time Tab */}
          <TabsContent value="screen-time" className="space-y-6" data-testid="screen-time-content">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-purple-600" />Smart Screen Time Controls</CardTitle>
                <p className="text-sm text-gray-500">Configure fatigue monitoring and break reminders for your child's study sessions.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Screen Time Protection</p>
                    <p className="text-sm text-gray-500">Monitors fatigue and suggests breaks during study</p>
                  </div>
                  <button
                    onClick={() => setSscsSettings(s => ({ ...s, sscs_enabled: !s.sscs_enabled }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${sscsSettings.sscs_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    data-testid="sscs-toggle"
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${sscsSettings.sscs_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                {sscsSettings.sscs_enabled && (
                  <div className="space-y-4">
                    {/* Break Suggestion Timer */}
                    <div className="p-4 border rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <Timer className="h-4 w-4 text-amber-500" />
                        <label className="font-medium text-sm">Suggest Break After</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="15"
                          max="90"
                          step="5"
                          value={sscsSettings.suggest_break_minutes}
                          onChange={(e) => setSscsSettings(s => ({ ...s, suggest_break_minutes: parseInt(e.target.value) }))}
                          className="flex-1 accent-amber-500"
                          data-testid="suggest-break-slider"
                        />
                        <span className="text-sm font-bold text-amber-600 w-16 text-right">{sscsSettings.suggest_break_minutes} min</span>
                      </div>
                      <p className="text-xs text-gray-400">A gentle reminder will appear suggesting your child takes a break.</p>
                    </div>

                    {/* Enforce Lock Timer */}
                    <div className="p-4 border rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-red-500" />
                        <label className="font-medium text-sm">Enforce Break After</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="30"
                          max="120"
                          step="5"
                          value={sscsSettings.enforce_lock_minutes}
                          onChange={(e) => setSscsSettings(s => ({ ...s, enforce_lock_minutes: parseInt(e.target.value) }))}
                          className="flex-1 accent-red-500"
                          data-testid="enforce-lock-slider"
                        />
                        <span className="text-sm font-bold text-red-600 w-16 text-right">{sscsSettings.enforce_lock_minutes} min</span>
                      </div>
                      <p className="text-xs text-gray-400">Screen will lock and require a break before continuing.</p>
                    </div>

                    {/* Break Duration */}
                    <div className="p-4 border rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-green-500" />
                        <label className="font-medium text-sm">Break Duration</label>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="5"
                          max="30"
                          step="5"
                          value={sscsSettings.break_duration_minutes}
                          onChange={(e) => setSscsSettings(s => ({ ...s, break_duration_minutes: parseInt(e.target.value) }))}
                          className="flex-1 accent-green-500"
                          data-testid="break-duration-slider"
                        />
                        <span className="text-sm font-bold text-green-600 w-16 text-right">{sscsSettings.break_duration_minutes} min</span>
                      </div>
                      <p className="text-xs text-gray-400">How long the enforced break lasts.</p>
                    </div>

                    <Button 
                      onClick={saveSscsSettings} 
                      disabled={sscsSaving}
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
                      data-testid="save-sscs-btn"
                    >
                      {sscsSaving ? 'Saving...' : 'Save Screen Time Settings'}
                    </Button>
                  </div>
                )}

                {/* Analytics Summary */}
                {sscsAnalytics && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2"><Brain className="h-4 w-4" /> Session Analytics (Last 7 Days)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-700">{sscsAnalytics.total_sessions || 0}</p>
                        <p className="text-xs text-gray-500">Sessions</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-700">{sscsAnalytics.total_answers || 0}</p>
                        <p className="text-xs text-gray-500">Questions</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-2xl font-bold text-green-700">{Math.round(sscsAnalytics.avg_accuracy || 0)}%</p>
                        <p className="text-xs text-gray-500">Avg Accuracy</p>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-700">{sscsAnalytics.breaks_taken || 0}</p>
                        <p className="text-xs text-gray-500">Breaks Taken</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="badges" className="space-y-6">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Award className="h-5 w-5" />Badges & Achievements</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {streakData?.badges && streakData.badges.length > 0 ? (
                    streakData.badges.map((badge, idx) => {
                      const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                      return (
                        <div key={idx} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 text-center border-2 border-purple-200">
                          <div className="bg-gradient-to-br from-purple-500 to-indigo-500 text-white rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                            <IconComponent className="h-7 w-7" />
                          </div>
                          <h4 className="font-bold text-gray-800 text-sm">{badge.name}</h4>
                          <p className="text-xs text-gray-600 mt-1">{badge.description}</p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-full text-center py-8">
                      <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-600 mb-2">No badges yet!</h3>
                      <p className="text-gray-500">Encourage your child to practice regularly</p>
                    </div>
                  )}
                </div>

                <div className="mt-6 bg-gradient-to-r from-orange-100 to-red-100 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Flame className="h-8 w-8 text-orange-500" />
                      <div>
                        <p className="font-bold text-gray-800">Current Study Streak</p>
                        <p className="text-sm text-gray-600">Keep the momentum going!</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-orange-600">{streakData?.currentStreak || 0}</p>
                      <p className="text-sm text-gray-500">days</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA - Book a Tutor */}
        <Card className="shadow-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-4 rounded-full"><Users className="h-10 w-10" /></div>
                <div>
                  <h2 className="text-2xl font-bold">Need Extra Support?</h2>
                  <p className="text-white/90">Book a 1:1 session with an expert tutor</p>
                </div>
              </div>
              <Button className="bg-white text-green-600 hover:bg-white/90 font-semibold px-8" onClick={() => navigate('/tutor-collaboration')} data-testid="book-tutor-cta">
                Find a Tutor <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Link Another Child Modal */}
      {showLinkForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Link Another Child</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={linkChild} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="childEmail2">Child's Email Address</Label>
                  <Input id="childEmail2" type="email" placeholder="Enter your child's email" value={childEmail} onChange={(e) => setChildEmail(e.target.value)} required />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setShowLinkForm(false)}>Cancel</Button>
                  <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={linkLoading}>{linkLoading ? 'Linking...' : 'Link Account'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ParentDashboard;
